import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { HttpParams } from '@angular/common/http';
import { forkJoin } from 'rxjs';
import { ChartData, ChartOptions } from 'chart.js';
import { JsonService } from '../_services/json.service';
import { LoadingService } from '../_services/loading.service';
import { LoggerService } from '../_services/logger.service';
import { GlobalService } from '../_services/global.service';
import { IndexedDbService } from '../_services/indexed-db.service';

@Component({
  selector: 'app-home',
  templateUrl: './home.component.html',
  styleUrls: ['./home.component.scss']
})
export class HomeComponent implements OnInit {
  filtro = { ano: new Date().getFullYear().toString(), mes: '', pais: '', provedor: '' };

  listAnos: string[] = [];
  listMeses: string[] = [];
  listPaises: string[] = [];
  listProvedores: string[] = [];

  // ── Estado independente — chart Prescrições ──────────────────────
  isDrilledPrescricoes = false;
  paisPrescricoes      = '';

  // ── Estado independente — chart Revisadas ───────────────────────
  isDrilledRevisadas = false;
  paisRevisadas      = '';
  isDrilledRevisor   = false;
  mesRevisadas       = '';

  // ── Loading independente ─────────────────────────────────────────
  isAdminUser = false;

  isLoadingPrescricoes = false;
  isLoadingRevisadas   = false;

  semDadosPrescricoes = false;
  semDadosRevisadas   = false;

  nomePaisesPrescricoes: string[] = [];
  nomePaisesRevisadas:   string[] = [];

  prescricoesBarColors: string[] = [];
  revisadasBarColors:   string[] = [];

  // Paleta profissional e suave — baseada no padrão Tableau/D3 category10
  private readonly PALETTE = [
    '#4E79A7', '#F28E2B', '#59A14F', '#E15759', '#76B7B2',
    '#EDC948', '#B07AA1', '#FF9DA7', '#9C755F', '#BAB0AC',
    '#86BCB6', '#D4A6C8', '#FFBE7D', '#8CD17D', '#499894',
    '#F1CE63', '#D37295', '#A0CBE8', '#FABFD2', '#B6992D'
  ];

  private getColors(count: number): string[] {
    return Array.from({ length: count }, (_, i) => this.PALETTE[i % this.PALETTE.length]);
  }

  prescricoesChartData: ChartData<'bar'> = {
    labels: [],
    datasets: [{ data: [], label: 'Total', backgroundColor: [], borderRadius: 4 }]
  };

  prescricoesChartOptions: ChartOptions<'bar'> = {
    responsive: true,
    maintainAspectRatio: false,
    onHover: (_e: any, els: any[]) => {
      const t = _e?.native?.target as HTMLCanvasElement | null;
      if (t) t.style.cursor = els.length > 0 && !this.isDrilledPrescricoes ? 'pointer' : 'default';
    },
    plugins: {
      legend: {
        display: true,
        position: 'bottom',
        labels: {
          boxWidth: 12,
          padding: 10,
          font: { size: 11 },
          generateLabels: (chart) => {
            const labels = chart.data.labels as string[];
            const colors = this.prescricoesBarColors;
            if (!labels?.length) return [];
            return labels.map((lbl, i) => ({
              text: lbl as string,
              fillStyle: colors[i] ?? '#aaa',
              strokeStyle: colors[i] ?? '#aaa',
              hidden: false,
              datasetIndex: 0,
              index: i
            }));
          }
        }
      },
      tooltip: {
        callbacks: {
          title: (items) => {
            if (this.isDrilledPrescricoes) return items[0]?.label ?? '';
            return this.nomePaisesPrescricoes[items[0]?.dataIndex ?? 0] ?? items[0]?.label ?? '';
          }
        }
      }
    },
    scales: {
      x: {
        ticks: {
          color: (ctx: any) => this.prescricoesBarColors[ctx.index] ?? '#555',
          font: { weight: 600, size: 11 }
        }
      },
      y: { beginAtZero: true, ticks: { precision: 0 } }
    }
  };

  revisadasChartData: ChartData<'bar'> = {
    labels: [],
    datasets: [{ data: [], label: 'Total', backgroundColor: [], borderRadius: 4 }]
  };

  revisadasChartOptions: ChartOptions<'bar'> = {
    responsive: true,
    maintainAspectRatio: false,
    onHover: (_e: any, els: any[]) => {
      const t = _e?.native?.target as HTMLCanvasElement | null;
      if (t) t.style.cursor = els.length > 0 && !this.isDrilledRevisor ? 'pointer' : 'default';
    },
    plugins: {
      legend: {
        display: true,
        position: 'bottom',
        labels: {
          boxWidth: 12,
          padding: 10,
          font: { size: 11 },
          generateLabels: (chart) => {
            const labels = chart.data.labels as string[];
            const colors = this.revisadasBarColors;
            if (!labels?.length) return [];
            return labels.map((lbl, i) => ({
              text: lbl as string,
              fillStyle: colors[i] ?? '#aaa',
              strokeStyle: colors[i] ?? '#aaa',
              hidden: false,
              datasetIndex: 0,
              index: i
            }));
          }
        }
      },
      tooltip: {
        callbacks: {
          title: (items) => {
            if (this.isDrilledRevisor || this.isDrilledRevisadas) return items[0]?.label ?? '';
            return this.nomePaisesRevisadas[items[0]?.dataIndex ?? 0] ?? items[0]?.label ?? '';
          }
        }
      }
    },
    scales: {
      x: {
        ticks: {
          color: (ctx: any) => this.revisadasBarColors[ctx.index] ?? '#555',
          font: { weight: 600, size: 11 }
        }
      },
      y: { beginAtZero: true, ticks: { precision: 0 } }
    }
  };

  constructor(
    private jsonService: JsonService,
    private loadingService: LoadingService,
    private logger: LoggerService,
    private globalService: GlobalService,
    private db: IndexedDbService,
    private router: Router
  ) { }

  ngOnInit(): void {
    localStorage.removeItem('listIDs');
    localStorage.removeItem('filtro');

    this.db.getData('id_token').then(idToken => {
      if (idToken) {
        this.jsonService.unlockAllPage().subscribe(
          () => {},
          erro => { this.logger.error(erro); }
        );
      }
    });

    this.getUserPermissions();
  }

  getUserPermissions(): void {
    this.jsonService.getUserPermission().subscribe(
      resp => {
        this.globalService.setPermissions(resp);
        this.isAdminUser = this.globalService.isAdmin()
          || this.globalService.isAdminGrupo()
          || this.globalService.isAdminGlobalGrupo();

        // Revisor comum não tem acesso ao Dashboard — só à fila de revisão.
        if (!this.isAdminUser) {
          this.router.navigate(['/receitas-pending-issues']);
          return;
        }

        this.loadFilterLists();
      },
      erro => { this.logger.error('Erro ao carregar permissões do usuário', erro); }
    );
  }

  loadFilterLists(): void {
    this.loadingService.show();
    forkJoin([
      this.jsonService.getAno(),
      this.jsonService.getMes(),
      this.jsonService.getPais(),
      this.jsonService.getProvedor(),
    ]).subscribe({
      next: ([anos, meses, paises, provedores]) => {
        this.listAnos       = anos       || [];
        this.listMeses      = meses      || [];
        this.listPaises     = paises     || [];
        this.listProvedores = provedores || [];
        this.loadingService.hide();
        this.loadPrescricoesPorPais();
        this.loadRevisadasPorPais();
      },
      error: erro => {
        this.logger.error('Erro ao carregar filtros', erro);
        this.loadingService.hide();
        this.loadPrescricoesPorPais();
        this.loadRevisadasPorPais();
      }
    });
  }

  // ── Helpers ──────────────────────────────────────────────────────

  private baseParams(): HttpParams {
    let p = new HttpParams();
    if (this.filtro.ano)      p = p.append('ano',      this.filtro.ano);
    if (this.filtro.provedor) p = p.append('provedor', this.filtro.provedor);
    return p;
  }

  private applyChart(
    rows:    any[],
    type:    'prescricoes' | 'revisadas',
    labelFn: (d: any) => string,
    nameFn:  (d: any) => string = labelFn
  ): void {
    const labels = rows.map(labelFn);
    const nomes  = rows.map(nameFn);
    const values = rows.map((d: any) => d.total);
    const colors = this.getColors(labels.length);

    if (type === 'prescricoes') {
      this.nomePaisesPrescricoes = nomes;
      this.prescricoesBarColors  = colors;
      this.semDadosPrescricoes   = labels.length === 0;
      this.prescricoesChartData  = {
        ...this.prescricoesChartData,
        labels,
        datasets: [{ ...this.prescricoesChartData.datasets[0], data: values, backgroundColor: colors, hoverBackgroundColor: colors }]
      };
    } else {
      this.nomePaisesRevisadas = nomes;
      this.revisadasBarColors  = colors;
      this.semDadosRevisadas   = labels.length === 0;
      this.revisadasChartData  = {
        ...this.revisadasChartData,
        labels,
        datasets: [{ ...this.revisadasChartData.datasets[0], data: values, backgroundColor: colors, hoverBackgroundColor: colors }]
      };
    }
  }

  // ── Prescrições: por País ────────────────────────────────────────

  loadPrescricoesPorPais(): void {
    this.isLoadingPrescricoes = true;
    let p = this.baseParams();
    if (this.filtro.mes)  p = p.append('mes',  this.filtro.mes);
    if (this.filtro.pais) p = p.append('pais', this.filtro.pais);

    this.jsonService.getDashboardPrescricoesPorPais(p).subscribe({
      next: data => {
        this.applyChart(data || [], 'prescricoes', d => d.codigoPais || d.nomePais, d => d.nomePais);
        this.isLoadingPrescricoes = false;
      },
      error: erro => {
        this.logger.error('Erro prescrições por país', erro);
        this.isLoadingPrescricoes = false;
      }
    });
  }

  drillPrescricoes(country: string): void {
    this.isDrilledPrescricoes = true;
    this.paisPrescricoes      = country;
    this.loadPrescricoesPorMes();
  }

  voltarPrescricoes(): void {
    this.isDrilledPrescricoes = false;
    this.paisPrescricoes      = '';
    this.loadPrescricoesPorPais();
  }

  private loadPrescricoesPorMes(): void {
    this.isLoadingPrescricoes = true;
    let p = this.baseParams();
    if (this.paisPrescricoes) p = p.append('pais', this.paisPrescricoes);

    this.jsonService.getDashboardPrescricoesPorMes(p).subscribe({
      next: data => {
        this.applyChart(data || [], 'prescricoes', d => d.label);
        this.isLoadingPrescricoes = false;
      },
      error: erro => {
        this.logger.error('Erro prescrições por mês', erro);
        this.isLoadingPrescricoes = false;
      }
    });
  }

  // ── Revisadas: por País ──────────────────────────────────────────

  loadRevisadasPorPais(): void {
    this.isLoadingRevisadas = true;
    let p = this.baseParams();
    if (this.filtro.mes)  p = p.append('mes',  this.filtro.mes);
    if (this.filtro.pais) p = p.append('pais', this.filtro.pais);

    this.jsonService.getDashboardRevisadasPorPais(p).subscribe({
      next: data => {
        this.applyChart(data || [], 'revisadas', d => d.codigoPais || d.nomePais, d => d.nomePais);
        this.isLoadingRevisadas = false;
      },
      error: erro => {
        this.logger.error('Erro revisadas por país', erro);
        this.isLoadingRevisadas = false;
      }
    });
  }

  drillRevisadas(country: string): void {
    this.isDrilledRevisadas = true;
    this.paisRevisadas      = country;
    this.loadRevisadasPorMes();
  }

  voltarRevisadas(): void {
    this.isDrilledRevisadas = false;
    this.isDrilledRevisor   = false;
    this.paisRevisadas      = '';
    this.mesRevisadas       = '';
    this.loadRevisadasPorPais();
  }

  private loadRevisadasPorMes(): void {
    this.isLoadingRevisadas = true;
    let p = this.baseParams();
    if (this.paisRevisadas) p = p.append('pais', this.paisRevisadas);

    this.jsonService.getDashboardRevisadasPorMes(p).subscribe({
      next: data => {
        this.applyChart(data || [], 'revisadas', d => d.label);
        this.isLoadingRevisadas = false;
      },
      error: erro => {
        this.logger.error('Erro revisadas por mês', erro);
        this.isLoadingRevisadas = false;
      }
    });
  }

  // ── Revisadas: por Revisor ───────────────────────────────────────

  drillRevisor(mes: string): void {
    this.isDrilledRevisor = true;
    this.mesRevisadas     = mes;
    this.loadRevisadasPorRevisor();
  }

  voltarRevisor(): void {
    this.isDrilledRevisor = false;
    this.mesRevisadas     = '';
    this.loadRevisadasPorMes();
  }

  private loadRevisadasPorRevisor(): void {
    this.isLoadingRevisadas = true;
    let p = this.baseParams();
    if (this.paisRevisadas) p = p.append('pais', this.paisRevisadas);
    if (this.mesRevisadas)  p = p.append('mes',  this.mesRevisadas);

    this.jsonService.getDashboardRevisadasPorRevisor(p).subscribe({
      next: data => {
        this.applyChart(data || [], 'revisadas', d => d.label);
        this.isLoadingRevisadas = false;
      },
      error: erro => {
        this.logger.error('Erro revisadas por revisor', erro);
        this.isLoadingRevisadas = false;
      }
    });
  }

  // ── Clique nos charts ────────────────────────────────────────────

  onPrescricoesClick(event: { active?: any[] }): void {
    if (!event.active?.length || this.isDrilledPrescricoes) return;
    const idx     = event.active[0].index;
    const country = this.nomePaisesPrescricoes[idx];
    if (country) this.drillPrescricoes(country);
  }

  onRevisadasClick(event: { active?: any[] }): void {
    if (!event.active?.length) return;
    const idx = event.active[0].index;

    if (!this.isDrilledRevisadas) {
      const country = this.nomePaisesRevisadas[idx];
      if (country) this.drillRevisadas(country);
    } else if (!this.isDrilledRevisor) {
      const mes = this.revisadasChartData.labels?.[idx] as string;
      if (mes) this.drillRevisor(mes);
    }
  }

  // ── Filtros ──────────────────────────────────────────────────────

  atualizaFiltro(): void {
    if (this.isDrilledRevisor) {
      this.loadRevisadasPorRevisor();
    } else if (this.isDrilledRevisadas) {
      this.loadRevisadasPorMes();
    } else {
      this.loadRevisadasPorPais();
    }

    if (this.isDrilledPrescricoes) {
      this.loadPrescricoesPorMes();
    } else {
      this.loadPrescricoesPorPais();
    }
  }

  clearFiltros(): void {
    this.filtro = { ano: new Date().getFullYear().toString(), mes: '', pais: '', provedor: '' };
    this.atualizaFiltro();
  }
}
